import { Check, LayoutDashboard, Undo2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  requestHomeLayoutApprove,
  requestHomeLayoutUndo,
} from "@/features/dashboard/home/kaiHomeLayoutBridge";
import type { HomeLayoutReview } from "@/types/home";

interface KaiHomeLayoutReviewCardProps {
  review: HomeLayoutReview;
}

/**
 * Inline review card shown at the end of a Kai "Edit with Kai" turn. Kai's
 * widget changes are already previewed live on the dashboard grid; this card
 * (and the matching banner in the Home edit header) lets the user approve
 * (persist) or undo (revert to the pre-session snapshot). Both controls relay
 * to `useHomeEditor` via window events so the two entry points stay in sync.
 */
export function KaiHomeLayoutReviewCard({ review }: KaiHomeLayoutReviewCardProps) {
  return (
    <div className="glass-panel-strong clay-strong w-full max-w-[min(100%,38rem)] rounded-2xl px-4 py-3.5">
      <div className="flex items-center gap-2">
        <LayoutDashboard className="size-4 shrink-0 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">
          Review home layout
        </h3>
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {review.summary || "Kai updated your home layout."}
      </p>
      {review.widget_count > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          {review.widget_count} widget{review.widget_count === 1 ? "" : "s"} on
          the board. Changes are a preview until you approve.
        </p>
      )}

      <div className="mt-3.5 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          className="h-8 gap-1.5 rounded-xl px-3 text-xs"
          onClick={() => requestHomeLayoutApprove()}
        >
          <Check className="size-3.5" />
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 rounded-xl px-3 text-xs"
          onClick={() => requestHomeLayoutUndo()}
        >
          <Undo2 className="size-3.5" />
          Undo
        </Button>
      </div>
    </div>
  );
}
